import { generateId } from '@/utils/id';
import { MindMapDocument } from '@/model/document';
import { db, type MapRecord } from '@/persistence/indexedDb';

export interface MapListEntry {
  id: string;
  title: string;
  updatedAt: number;
  /** Present when the map is shared with the user (not owned). */
  ownerEmail?: string;
  /** The user's role on this map. */
  role?: 'owner' | 'editor' | 'viewer';
}

/**
 * Creates a new mind map, initializes it with a root node,
 * saves to IndexedDB, and returns the map ID.
 */
export async function createNewMap(title: string): Promise<string> {
  const id = generateId();
  const mapDoc = new MindMapDocument();
  mapDoc.initialize(title);
  const state = mapDoc.encodeState();
  await db.saveMap(id, title, state);
  return id;
}

/**
 * Loads a saved map's Yjs state from IndexedDB and applies it
 * to the given MindMapDocument.
 */
export async function openMap(id: string, mapDoc: MindMapDocument): Promise<void> {
  const record: MapRecord | undefined = await db.loadMap(id);
  if (!record) {
    throw new Error(`Map with id "${id}" not found`);
  }
  mapDoc.applyState(record.yjsState);
}

/**
 * Returns the list of all saved maps ordered by most recently updated.
 */
export async function getMapList(): Promise<MapListEntry[]> {
  const records = await db.listMaps();
  return records.map((r) => ({
    id: r.id,
    title: r.title,
    updatedAt: r.updatedAt,
  }));
}
